import { getAwsCredentials } from "src/authentication/cognito";
 const AWS = require("aws-sdk");
 
 const TABLE_NAME = 'AWSResourceMetadata';
 const INDEX_NAME = 'accountIdResourceTypeKey-index';
 const SORT_KEY = 'ResourceMetaData'; 
 /**
  * Returns AWSResourceMetaData record given ID
  * @param id
  * @returns {Promise<DocumentClient.AttributeMap[]>}
  */
 export const getAwsResourceMetadata = async (id) => {
     console.log(`Reading resources for AWSResourcesMetadata : ${id} for projected expression`);
     await getAwsCredentials();
 
     const docClient = new AWS.DynamoDB.DocumentClient({ region: 'us-east-1' ,
         accessKey: AWS.config.credentials.accessKeyId,
         secretKey: AWS.config.credentials.secretAccessKey,
         sessionToken: AWS.config.credentials.sessionToken,
     });

     let firstQuery = true;
     let lastKey;
     let queryResults = []

     while(firstQuery || lastKey) {
         firstQuery = false;
         let params = {
             TableName: TABLE_NAME,
             KeyConditionExpression: 'AccountIdResourceTypePartitionKey = :id',
             ExpressionAttributeValues: {
                 ':id': id
             },
             ...(lastKey && {
                 ExclusiveStartKey: lastKey
             })
         }
         try {
             const data = await docClient.query(params).promise();
             console.log(`Data read from the AWSResourcesMetadata table for AccountIdResourceTypePartitionKey ${id} : `, data.Items);
             lastKey = data.LastEvaluatedKey;
             queryResults = [...data.Items];
         } catch (err) {
             console.log(`Error occurred while fetching entity data for ${id} : `, err);
             throw err;
         }
     }

     return queryResults;
 }
 
 /**
  * Returns list of AWSResourceMetaData given list of AWSResourceMetaData Ids
  * @param ids
  * @returns {Promise<DocumentClient.ItemResponse[]>}
  */
  export const transactGetAwsResourceMetadata = async (ids) => {
    await getAwsCredentials();

    const docClient = new AWS.DynamoDB.DocumentClient({ region: 'us-east-1' ,
        accessKey: AWS.config.credentials.accessKeyId,
        secretKey: AWS.config.credentials.secretAccessKey,
        sessionToken: AWS.config.credentials.sessionToken,
    });


    let transactItems = [];
    for(let id of ids) {
        transactItems.push({
            Get: {
                TableName: TABLE_NAME,
                Key: {
                    accountIdResourceTypeKey: id,
                    resourceIdentifier: SORT_KEY
                },
                IndexName: INDEX_NAME,
                KeyConditionExpression: 'AccountIdResourceTypePartitionKey = :id',
                ExpressionAttributeValues: {
                    ':id': id
                }
            }
        })
    }

    let params = { TransactItems: transactItems }

    try {
        const data = await docClient.transactGet(params).promise();
        console.log(`Data read from the AWSResourcesMetadata table for AccountIdResourceTypePartitionKey ${ids} : `, data?.Responses);
        return data.Responses;
    } catch (err) {
        console.log(`Error occurred while fetching entity data for ${ids} : `, err);
        throw err;
    }
}

/**
 * Bulk transact update operations given list of resource items of format { id: partitionKey, fields: { field : value, ...} }
 * @param resourceItems
 * @returns {Promise<void>}
 */
export const transactUpdateAwsResourceMetadata = async (resourceItems) => {
    await getAwsCredentials();

    const docClient = new AWS.DynamoDB.DocumentClient({ region: 'us-east-1' ,
        accessKey: AWS.config.credentials.accessKeyId,
        secretKey: AWS.config.credentials.secretAccessKey,
        sessionToken: AWS.config.credentials.sessionToken,
    });

    let transactItems = [];
    let transactIds = [];
    let ids = [];
    let writeRequests = [];
    for(let resourceItem of resourceItems) {
        let { AccountIdResourceTypePartitionKey, ResourceId, ...otherFields } = resourceItem;
        ids.push(AccountIdResourceTypePartitionKey);
        transactItems.push(buildAwsResourceMetadataUpdateRequest(AccountIdResourceTypePartitionKey, ResourceId, otherFields));
        // transactWrite API limits to 25 items per transaction, split them into promise chunks
        if(transactItems.length === 25) {
            let params = { TransactItems: transactItems };
            writeRequests.push(docClient.transactWrite(JSON.parse(JSON.stringify(params))).promise());
            transactIds.push([...ids]);
            transactItems = [];
            ids = [];
        }
    }

    if(transactItems.length > 0) {
        let params = { TransactItems: transactItems }
        writeRequests.push(docClient.transactWrite(JSON.parse(JSON.stringify(params))).promise());
        transactIds.push([...ids]);
    }

    Promise.allSettled(writeRequests).then(results => {
        for(let i = 0; i < results.length; i++) {
            if(results[i].status === "rejected") {
                console.error(`Error updating AWSResourceMetadata for the following record Ids: ${transactIds[i]}`);
            } else {
                console.log(`Successfully updated AWSResourceMetadata for the following record Ids: ${transactIds[i]}`);
            }
        }
    })
}

/**
 * Helper method that builds the update request for the resource.
 * @param id
 * @param fields
 * @returns {{Update: {TableName: string, UpdateExpression: string, Key: {resourceIdentifier: string, AccountIdResourceTypePartitionKey}, ExpressionAttributeValues: {}}}}
 */
const buildAwsResourceMetadataUpdateRequest = (pk, sk, fields) => {
    let [updateExpression, expressionAttributeValues] = buildExpressions(fields);
    let params = {
        TableName: TABLE_NAME,
        Key: {
            AccountIdResourceTypePartitionKey: pk,
            ResourceId: sk
        },
        UpdateExpression: updateExpression,
        ExpressionAttributeValues: expressionAttributeValues
    }
    return { Update: params };
}

/**
 * helper method that builds the update expression given object containing fields and values to be updated.
 * @param fieldsObj
 * @returns {[string, {}]}
 */
const buildExpressions = (fieldsObj) => {
    let updateExpression = ["set", " "];
    let expressionAttributeValues = {};
    for(let [key, value] of Object.entries(fieldsObj)) {
        updateExpression.push(`${key} = :${key}`);
        updateExpression.push(',');
        expressionAttributeValues[`:${key}`] = value;
    }
    // Remove last comma in string
    updateExpression.pop();
    return [updateExpression.join(""), expressionAttributeValues];
}